import { Component, EventEmitter, OnDestroy, OnInit, ViewChild, ElementRef, HostListener } from '@angular/core';
import { AbstractControl, FormBuilder, FormControl, FormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { CreateTaskService, TaskResponseDTO } from './service/create-task.service';
import { debounceTime, distinctUntilChanged, finalize, Subject, take, takeUntil } from 'rxjs';
import { SnackbarService } from 'src/app/shared/snackbar/snackbar.service';
import { TranslateService } from '@ngx-translate/core';
import { ReusableSearcherComponent } from 'src/app/shared/reusable-searcher/reusable-searcher.component';
import { TaskEventService } from '../../services/task-event.service';

const DATE_FORMAT = {
  YEAR_MONTH_DAY: 'YYYY-MM-DD',
} as const;

const VALIDATION_CONFIG = {
  MIN_SEARCH_LENGTH: 1,
  SEARCH_DEBOUNCE_MS: 400,
  NAME_MAX_LENGTH: 100,
  DESCRIPTION_MAX_LENGTH: 500,
} as const;

function expirationAfterExecutionValidator(): ValidatorFn {
  return (control: AbstractControl): ValidationErrors | null => {
    const form = control as FormGroup;
    const executionDate = form.get('executionDate')?.value;
    const expirationDate = form.get('expirationDate')?.value;

    if (!executionDate || !expirationDate) {
      return null;
    }

    const execution = new Date(executionDate);
    const expiration = new Date(expirationDate);

    execution.setHours(0, 0, 0, 0);
    expiration.setHours(0, 0, 0, 0);

    return expiration < execution ? { expirationBeforeExecution: true } : null;
  };
}

export interface TaskCreateDTO {
  Name: string;
  Description: string;
  VehicleDescription: string;
  LicensePlate: string;
  ClientGuid: string;
  RealizationDate: string;
  ExpirationDate: string;
}

export interface TaskFormValues {
  name: string;
  description: string;
  licensePlate: string;
  vehicle: ClientInfoDTO | null;
  clientId: string;
  clientName: string;
  brandModel: string;
  executionDate: Date | null;
  expirationDate: Date | null;
}

export interface TaskFormGroup {
  name: FormControl<string>;
  description: FormControl<string>;
  licensePlate: FormControl<string>;
  vehicle: FormControl<ClientInfoDTO | null>;
  brandModel: FormControl<string>
  clientId: FormControl<string>;
  clientName: FormControl<string>;
  executionDate: FormControl<Date | null>;
  expirationDate: FormControl<Date | null>;
}

export interface ClientInfoDTO {
  clientFullName: string;
  id: string;
  licensePlate: string;
}

@Component({
  selector: 'app-create-task-modal',
  templateUrl: './create-task-modal.component.html',
  styleUrls: ['./create-task-modal.component.scss']
})
export class CreateTaskModalComponent implements OnInit, OnDestroy {
  taskId?: string;
  taskForm!: FormGroup<TaskFormGroup>;
  saveLoading = false;
  loadingTask = false;
  result: EventEmitter<boolean> = new EventEmitter<boolean>();
  searchingVehicles = false;
  initialValues?: Partial<TaskFormValues>;

  private readonly destroy$ = new Subject<void>();
  private readonly vehicleSearch$ = new Subject<string>();
  private initialFormValue?: TaskFormValues;

  @ViewChild('vehicleSearcher') vehicleSearcher!: ReusableSearcherComponent<ClientInfoDTO>;
  @ViewChild('nameInput') nameInput?: ElementRef<HTMLInputElement>;

  constructor(
    private readonly bsModalRef: BsModalRef,
    private readonly fb: FormBuilder,
    private readonly createTaskService: CreateTaskService,
    private readonly snackbarService: SnackbarService,
    private readonly translate: TranslateService,
    private readonly taskEventService: TaskEventService,
  ) { }

  ngOnInit(): void {
    this.initializeForm();

    if (this.initialValues) {
      this.applyInitialValues(this.initialValues);
    }

    if (this.taskId) {
      this.loadTaskData();
    } else {
      this.focusNameInput();
      this.storeInitialFormValue();
    }
  }

  private applyInitialValues(values: Partial<TaskFormValues>): void {
    const safeValues: Partial<TaskFormValues> = {};

    if (values.name) safeValues.name = values.name;
    if (values.description) safeValues.description = values.description;
    if (values.licensePlate) safeValues.licensePlate = values.licensePlate;
    if (values.vehicle) safeValues.vehicle = values.vehicle;
    if (values.brandModel) safeValues.brandModel = values.brandModel;
    if (values.clientId) safeValues.clientId = values.clientId;
    if (values.clientName) safeValues.clientName = values.clientName;
    if (values.executionDate) safeValues.executionDate = values.executionDate;
    if (values.expirationDate) safeValues.expirationDate = values.expirationDate;

    this.taskForm.patchValue(safeValues);
  }

  private focusNameInput(): void {
    setTimeout(() => this.nameInput?.nativeElement?.focus(), 200);
  }

  private storeInitialFormValue(): void {
    this.initialFormValue = this.taskForm.getRawValue();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  @HostListener('window:keydown', ['$event'])
  onKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Enter') {
      const target = event.target as HTMLElement;
      if (target.tagName === 'TEXTAREA') return;

      if (this.isFormValid && !this.saveLoading) {
        event.preventDefault();
        this.saveTask();
      }
    } else if (event.key === 'Escape') {
      if (!this.saveLoading) {
        event.preventDefault();
        this.onCancel();
      }
    }
  }

  onCloseButtonKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onCancel();
    }
  }

  get isEditMode(): boolean {
    return !!this.taskId;
  }

  get modalTitle(): string {
    return this.isEditMode
      ? this.translate.instant('myTasks.edit_task.title')
      : this.translate.instant('myTasks.create_task.title');
  }

  get saveButtonLabel(): string {
    if (this.saveLoading) {
      return this.translate.instant('common.saving');
    }
    return this.isEditMode
      ? this.translate.instant('common.update')
      : this.translate.instant('common.create');
  }

  private initializeForm(): void {
    this.taskForm = this.fb.nonNullable.group({
      name: ['', [Validators.required, Validators.maxLength(VALIDATION_CONFIG.NAME_MAX_LENGTH)]],
      description: ['', [Validators.required, Validators.maxLength(VALIDATION_CONFIG.DESCRIPTION_MAX_LENGTH)]],
      licensePlate: ['', [Validators.required]],
      vehicle: [null, [Validators.required]],
      brandModel: ['', [Validators.required]],
      clientId: ['', [Validators.required]],
      clientName: [''],
      executionDate: [null as Date | null, [Validators.required]],
      expirationDate: [null as Date | null, [Validators.required]]
    }, { validators: expirationAfterExecutionValidator() }) as FormGroup<TaskFormGroup>;

    this.setupVehicleSearch();
    this.setupDateValidation();
  }

  private setupVehicleSearch(): void {
    this.vehicleSearch$
      .pipe(
        debounceTime(VALIDATION_CONFIG.SEARCH_DEBOUNCE_MS),
        distinctUntilChanged(),
        takeUntil(this.destroy$)
      )
      .subscribe((query) => this.performVehicleSearch(query));
  }

  private setupDateValidation(): void {
    this.taskForm.get('executionDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.taskForm.updateValueAndValidity({ onlySelf: true, emitEvent: false }));

    this.taskForm.get('expirationDate')?.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => this.taskForm.updateValueAndValidity({ onlySelf: true, emitEvent: false }));
  }

  private loadTaskData(): void {
    if (!this.taskId) return;

    this.loadingTask = true;
    this.createTaskService.getTaskById(this.taskId)
      .pipe(
        take(1),
        finalize(() => {
          this.loadingTask = false;
        })
      )
      .subscribe({
        next: (task) => {
          this.populateForm(task);
          this.storeInitialFormValue();
        },
        error: (err) => this.handleLoadError(err),
      });
  }

  private populateForm(task: TaskResponseDTO): void {
    const vehicleInfo: ClientInfoDTO = {
      id: task.clientGuid,
      clientFullName: '',
      licensePlate: task.licensePlate,
    };

    this.taskForm.patchValue({
      name: task.name,
      description: task.description || '',
      licensePlate: task.licensePlate,
      vehicle: vehicleInfo,
      brandModel: task.vehicleDescription || '',
      clientId: task.clientGuid,
      clientName: '',
      executionDate: this.parseIsoDate(task.realizationDate),
      expirationDate: this.parseIsoDate(task.expirationDate),
    });
  }

  private handleLoadError(err: unknown): void {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    console.error('[CreateTaskModal] Error loading task:', errorMsg);
    this.snackbarService.showError(
      this.translate.instant('myTasks.edit_task.load_error'),
      this.translate.instant('common.error')
    );
    this.bsModalRef.hide();
  }

  onCancel(): void {
    if (this.hasUnsavedChanges()) {
      const confirmed = confirm(
        this.translate.instant('myTasks.create_task.unsaved_changes_warning')
      );
      if (!confirmed) return;
    }
    this.result.emit(false);
    this.bsModalRef.hide();
  }

  private hasUnsavedChanges(): boolean {
    if (!this.initialFormValue || this.taskForm.pristine) return false;
    const currentValue = this.taskForm.getRawValue();
    return JSON.stringify(currentValue) !== JSON.stringify(this.initialFormValue);
  }

  get isFormValid(): boolean {
    return this.taskForm.valid && !this.loadingTask;
  }

  get hasDateValidationError(): boolean {
    return (
      this.taskForm.errors?.['expirationBeforeExecution'] &&
      this.taskForm.get('executionDate')?.touched &&
      this.taskForm.get('expirationDate')?.touched
    ) ?? false;
  }

  get characterCount(): { name: number; description: number } {
    return {
      name: this.taskForm.get('name')?.value?.length ?? 0,
      description: this.taskForm.get('description')?.value?.length ?? 0,
    };
  }

  private buildTaskDTO(taskValue: TaskFormValues): TaskCreateDTO {
    return {
      Name: taskValue.name,
      Description: taskValue.description,
      VehicleDescription: taskValue.brandModel,
      LicensePlate: taskValue.licensePlate,
      ClientGuid: taskValue.clientId,
      RealizationDate: this.formatDateToString(taskValue.executionDate),
      ExpirationDate: this.formatDateToString(taskValue.expirationDate)
    };
  }

  saveTask(): void {
    if (this.taskForm.invalid) return;

    this.saveLoading = true;
    const body = this.buildTaskDTO(this.taskForm.getRawValue());

    const operation$ = this.isEditMode && this.taskId
      ? this.createTaskService.updateTask(this.taskId, body)
      : this.createTaskService.createTask(body);

    operation$
      .pipe(
        take(1),
        finalize(() => {
          this.saveLoading = false;
        })
      )
      .subscribe({
        next: () => this.handleSaveSuccess(),
        error: (err) => this.handleSaveError(err),
      });
  }

  private handleSaveSuccess(): void {
    const messageKey = this.isEditMode
      ? 'myTasks.edit_task.task_updated_success'
      : 'myTasks.create_task.task_created_success';

    this.snackbarService.showSuccess(
      this.translate.instant(messageKey),
      this.translate.instant('common.success')
    );

    // Emit task event to notify other components to refresh
    if (this.isEditMode) {
      this.taskEventService.publishTaskStatusChanged({
        taskId: this.taskId || '',
        newStatus: 1,
        timestamp: new Date(),
      });
    } else {
      this.taskEventService.publishTaskCreated({
        taskName: this.taskForm.get('name')?.value,
        timestamp: new Date(),
      });
    }

    this.result.emit(true);
    this.bsModalRef.hide();
  }

  private handleSaveError(err: unknown): void {
    const errorMsg = err instanceof Error ? err.message : 'Unknown error';
    console.error('[CreateTaskModal] Error saving task:', errorMsg);

    const messageKey = this.isEditMode
      ? 'myTasks.edit_task.task_update_error'
      : 'myTasks.create_task.task_creation_error';

    this.snackbarService.showError(
      this.translate.instant(messageKey),
      this.translate.instant('common.error')
    );
  }

  searchVehiclesSearcher(query: string): void {
    const trimmedQuery = query.trim();

    if (trimmedQuery.length < VALIDATION_CONFIG.MIN_SEARCH_LENGTH) {
      this.vehicleSearcher.setResults([]);
      return;
    }

    this.vehicleSearch$.next(trimmedQuery);
  }

  private performVehicleSearch(query: string): void {
    this.searchingVehicles = true;
    this.createTaskService.searchVehicle(query)
      .pipe(
        take(1),
        finalize(() => {
          this.searchingVehicles = false;
        })
      )
      .subscribe({
        next: (data) => this.vehicleSearcher.setResults(data),
        error: (err) => {
          console.error('[CreateTaskModal] Error searching vehicles:', err);
          this.vehicleSearcher.setResults([]);
        },
      });
  }

  selectVehicle(vehicle: ClientInfoDTO): void {
    this.taskForm.patchValue({
      licensePlate: vehicle.licensePlate,
      vehicle: vehicle,
      clientId: vehicle.id,
      clientName: vehicle.clientFullName
    });
    this.taskForm.markAsDirty();
  }

  private formatDateToString(date: Date | null): string {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseIsoDate(isoString: string): Date | null {
    if (!isoString) return null;
    try {
      const date = new Date(isoString);
      return isNaN(date.getTime()) ? null : date;
    } catch {
      return null;
    }
  }
}
